import { toPositiveInteger } from './sidebar-utils.js';

const sidebarRuntimeByFormId = new Map();

/**
* Responsibility:
* - Resolve the mutable runtime record for one configurator form id.
*
* Touches:
* - Reads and optionally initializes module-level sidebar runtime state.
*
* Guarantees:
* - Returns a stable runtime object keyed by positive form id.
*/
function getSidebarRuntime(formId, { create = true } = {}) {
	const normalizedFormId = String(toPositiveInteger(formId));
	if (normalizedFormId === '0') {
		return null;
	}

		if (!sidebarRuntimeByFormId.has(normalizedFormId) && create) {
			sidebarRuntimeByFormId.set(normalizedFormId, {
				app: null,
				maxVisitedPage: 0,
				requestedTargetPage: 0,
				expandedTargetPage: 0,
				currentPrimaryTargetPage: 0,
				manualSelection: false,
				renderSignature: '',
				syncFrameId: 0,
			});
		}

	return sidebarRuntimeByFormId.get(normalizedFormId) ?? null;
}

/**
* Responsibility:
* - Persist the active configurator app instance for one form id.
*
* Touches:
* - Writes module-level runtime state keyed by `app.config.formId`.
*
* Guarantees:
* - Later sidebar modules can resolve the freshest app instance without querying globals.
*/
export function rememberSidebarApp(app) {
	const formId = toPositiveInteger(app?.config?.formId);
	const runtimeState = getSidebarRuntime(formId);
	if (!runtimeState) {
		return;
	}

	runtimeState.app = app ?? null;
}

/**
* Responsibility:
* - Resolve the latest remembered configurator app for one form id.
*
* Touches:
* - Reads module-level sidebar runtime state.
*
* Guarantees:
* - Returns the stored app object or `null` when none is available.
*/
export function getSidebarApp(formId) {
	return getSidebarRuntime(formId, {
		create: false,
	})?.app ?? null;
}

/**
* Responsibility:
* - Read the sidebar max-visited page state for one form id.
*
* Touches:
* - Reads module-level sidebar runtime state only.
*
* Guarantees:
* - Returns a positive page number or `0`.
*/
export function readMaxVisitedPage(formId) {
	return toPositiveInteger(getSidebarRuntime(formId, {
		create: false,
	})?.maxVisitedPage);
}

/**
* Responsibility:
* - Resolve and update monotonic sidebar max-visited page state.
*
* Touches:
* - Writes module-level runtime state instead of transient form dataset state.
*
* Guarantees:
* - Never decreases the remembered max visited page for the current configurator session.
*/
export function resolveMaxVisitedPage(formId, currentPage) {
	const runtimeState = getSidebarRuntime(formId);
	if (!runtimeState) {
		return 0;
	}

	runtimeState.maxVisitedPage = Math.max(
		toPositiveInteger(runtimeState.maxVisitedPage),
		toPositiveInteger(currentPage)
	);

	return runtimeState.maxVisitedPage;
}

/**
* Responsibility:
* - Persist the remembered max-visited page state for one configurator form.
*
* Touches:
* - Writes module-level sidebar runtime state keyed by form id.
*
* Guarantees:
* - Stores only the provided normalized positive page number or `0`.
*/
export function writeMaxVisitedPage(formId, maxVisitedPage) {
	const runtimeState = getSidebarRuntime(formId);
	if (!runtimeState) {
		return;
	}

	runtimeState.maxVisitedPage = toPositiveInteger(maxVisitedPage);
}

/**
* Responsibility:
* - Read the remembered grouped accordion state for one configurator form.
*
* Touches:
* - Reads module-level runtime state keyed by form id.
*
* Guarantees:
* - Returns normalized expanded-target, current-primary-target, and manual-selection flags.
*/
export function readPrimaryAccordionState(formId) {
	const runtimeState = getSidebarRuntime(formId, {
		create: false,
	});

	return {
		expandedTargetPage: toPositiveInteger(runtimeState?.expandedTargetPage),
		currentPrimaryTargetPage: toPositiveInteger(runtimeState?.currentPrimaryTargetPage),
		manualSelection: runtimeState?.manualSelection === true,
	};
}

/**
* Responsibility:
* - Persist grouped accordion UI state for one configurator form.
*
* Touches:
* - Writes module-level runtime state keyed by form id.
*
* Guarantees:
* - Stores only normalized positive page numbers and strict booleans.
*/
export function writePrimaryAccordionState(formId, {
	expandedTargetPage = 0,
	currentPrimaryTargetPage = 0,
	manualSelection = false,
} = {}) {
	const runtimeState = getSidebarRuntime(formId);
	if (!runtimeState) {
		return;
	}

	runtimeState.expandedTargetPage = toPositiveInteger(expandedTargetPage);
	runtimeState.currentPrimaryTargetPage = toPositiveInteger(currentPrimaryTargetPage);
	runtimeState.manualSelection = manualSelection === true;
}

/**
* Responsibility:
* - Read the pending sidebar-requested GF target page for one configurator form.
*
* Touches:
* - Reads module-level runtime state only.
*
* Guarantees:
* - Returns a positive target page or `0`.
*/
export function readRequestedTargetPage(formId) {
	return toPositiveInteger(getSidebarRuntime(formId, {
		create: false,
	})?.requestedTargetPage);
}

/**
* Responsibility:
* - Persist the pending sidebar-requested GF target page for one configurator form.
*
* Touches:
* - Writes module-level runtime state used by the native GF pre-submission bridge.
*
* Guarantees:
* - Stores only positive target pages or `0`.
*/
export function writeRequestedTargetPage(formId, targetPage) {
	const runtimeState = getSidebarRuntime(formId);
	if (!runtimeState) {
		return;
	}

	runtimeState.requestedTargetPage = toPositiveInteger(targetPage);
}

/**
* Responsibility:
* - Read the last resolved sidebar render signature for one configurator form.
*
* Touches:
* - Reads module-level runtime state only.
*
* Guarantees:
* - Returns the stable signature string or an empty string when unavailable.
*/
export function readSidebarRenderSignature(formId) {
	return String(getSidebarRuntime(formId, {
		create: false,
	})?.renderSignature || '');
}

/**
* Responsibility:
* - Persist the last resolved sidebar render signature for one configurator form.
*
* Touches:
* - Writes module-level runtime state keyed by form id.
*
* Guarantees:
* - Stores a trimmed string signature for later render-skip checks.
*/
export function writeSidebarRenderSignature(formId, renderSignature) {
	const runtimeState = getSidebarRuntime(formId);
	if (!runtimeState) {
		return;
	}

	runtimeState.renderSignature = String(renderSignature || '');
}

/**
* Responsibility:
* - Read the queued animation-frame id used for sidebar sync throttling.
*
* Touches:
* - Reads module-level runtime state only.
*
* Guarantees:
* - Returns a non-zero animation-frame id only while a sync is queued.
*/
export function readSidebarSyncFrameId(formId) {
	return toPositiveInteger(getSidebarRuntime(formId, {
		create: false,
	})?.syncFrameId);
}

/**
* Responsibility:
* - Persist the queued animation-frame id used for sidebar sync throttling.
*
* Touches:
* - Writes module-level runtime state keyed by form id.
*
* Guarantees:
* - Stores only positive frame ids or `0`.
*/
export function writeSidebarSyncFrameId(formId, frameId) {
	const runtimeState = getSidebarRuntime(formId);
	if (!runtimeState) {
		return;
	}

	runtimeState.syncFrameId = toPositiveInteger(frameId);
}

/**
* Responsibility:
* - Tear down transient sidebar runtime state for one configurator form.
*
* Touches:
* - Cancels queued animation work before dropping module state.
*
* Guarantees:
* - Leaves no retained sidebar runtime state for the provided form id.
*/
export function resetSidebarRuntime(formId) {
	const runtimeState = getSidebarRuntime(formId, {
		create: false,
	});
	const normalizedFormId = String(toPositiveInteger(formId));
	if (!runtimeState || normalizedFormId === '0') {
		return;
	}

	if (runtimeState.syncFrameId > 0) {
		cancelAnimationFrame(runtimeState.syncFrameId);
	}

	sidebarRuntimeByFormId.delete(normalizedFormId);
}
